import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Container } from "../../styled/CommonStyledComponents";

/**
 * A CSS-drawn recreation of the MajurAI flow. Six documents (email,
 * WhatsApp, customs portal, spreadsheets...) sit scattered around the
 * window; as soon as the page loads, an auto-playing sweep pulls them into
 * the AI layer at the center (direct DOM ref mutation per animation frame,
 * no scroll interaction required). Once all six land, the window becomes
 * the MajurAI chat (ask a question -> get a data breakdown -> get a map
 * opened alongside the chat), which then loops on a timer. Styled to match
 * the real app (frontend-infoss AiChatPage.tsx): cream page background,
 * blue user bubble, plain-text assistant replies, camel loading mark.
 */

const NAVY = "#0b1c75";
const INDIGO = "#4c5cf0";
const GOOD = "#12b07a";
const WARN = "#e08a2c";

const C = {
  pageBg: "#f8f7f2",
  divider: "#edebe5",
  text: "#1a1a1a",
  textSub: "#666666",
  textMuted: "#999999",
  userBubbleBg: "#0b57d0",
  chrome: "#f6f8fd",
  hairline: "#e4eaf6"
};

/* Scattered spots around the window's edges — a mix of top-left,
   top-center, top-right, plus a couple lower down so it reads as
   "documents everywhere". Values are percentages relative to FrameWrap;
   negative/>100 sit outside it. Each sweeps into the AI layer at its own
   staggered point in the scroll. */
const DOC_SOURCES = [
  { label: "Email", x: -14, y: 14, rot: -10 },
  { label: "Excel Sheets", x: 22, y: -22, rot: 8 },
  { label: "Customs Portal", x: 50, y: -28, rot: -6 },
  { label: "WhatsApp", x: 78, y: -22, rot: 11 },
  { label: "WMS Exports", x: 114, y: 14, rot: -14 },
  { label: "Shipping Docs", x: 106, y: 88, rot: 9 }
];

const LAND_X = 50;
const LAND_Y = 54;

const ACCOUNTS = [
  { account: "PRI UID Jawa Timur", lanes: 16, shipment: "210", category: "High" as const },
  { account: "PRI UID Sumatera Utara", lanes: 11, shipment: "154", category: "Medium" as const },
  { account: "PRI UID Jawa Barat", lanes: 7, shipment: "98", category: "Low" as const }
];

const ACCOUNT_TONE = { High: "good", Medium: "warn", Low: "neutral" } as const;

const ROUTES = [
  { route: "Jakarta → Surabaya", category: "Backhaul", value: "-$18K / mo" },
  { route: "Medan → Batam", category: "Triangular", value: "-$5K / mo" },
  { route: "Makassar → Balikpapan", category: "Backhaul", value: "-$2.7K / mo" }
];

const ROUTE_TONE = { Backhaul: "neutral", Triangular: "info" } as const;

const NODES = [
  { name: "Cikarang", tag: "Over-stocked", tone: "warn" as const, x: 22, y: 35 },
  { name: "Cirebon", tag: "Short", tone: "info" as const, x: 43, y: 44 },
  { name: "Solo", tag: "Short", tone: "info" as const, x: 72, y: 59 },
  { name: "Surabaya", tag: "Over-stocked", tone: "warn" as const, x: 90, y: 53 }
];

const TRANSFERS = ["1,100 units", "900 units", "1,500 units"];

const QUERY = "Evaluate my current operations and tell me where we can improve.";

/* 0 = query, 1 = account ranking, 2 = route breakdown, 3 = follow-up
   question, 4 = savings found, 5 = map opens. Starts once every document
   has swept into the AI layer; loops on a timer after that. */
const STAGE_DURATIONS = [1500, 2200, 2500, 1200, 2500, 3600];

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const CLEAR_WINDOW_END = 0.62;
const CHAT_IN_START = 0.78;
const CHAT_IN_END = 0.98;
const INTRO_DURATION_MS = 2900;

const AISearchSection = () => {
  const docRefs = useRef<Array<HTMLDivElement | null>>([]);
  const chatLayerRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);

  const [aiLabel, setAiLabel] = useState("AI Layer");
  const [showChat, setShowChat] = useState(false);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const el = messageListRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [stage, showChat]);

  useEffect(() => {
    const labelRef = { current: "AI Layer" };
    let doneFlag = false;
    let startTime: number | null = null;
    let frame: number;

    const tick = (now: number) => {
      if (doneFlag) return;
      if (startTime === null) startTime = now;
      const p = clamp01((now - startTime) / INTRO_DURATION_MS);

      DOC_SOURCES.forEach((doc, index) => {
        const el = docRefs.current[index];
        if (!el) return;

        const start = (index / DOC_SOURCES.length) * 0.4;
        const end = start + 0.3;
        const localT = clamp01((p - start) / (end - start));
        const eased = easeOutCubic(localT);

        const x = lerp(doc.x, LAND_X, eased);
        const y = lerp(doc.y, LAND_Y, eased);
        const rot = lerp(doc.rot, 0, eased);
        const scale = lerp(1, 0.25, eased);

        const fadeOut = clamp01((localT - 0.7) / 0.3);
        const opacity = 1 - fadeOut;

        el.style.left = `${x}%`;
        el.style.top = `${y}%`;
        el.style.transform = `translate(-50%, -50%) rotate(${rot}deg) scale(${scale})`;
        el.style.opacity = String(opacity);
      });

      const nextLabel = p < CLEAR_WINDOW_END ? "AI Layer" : "Learned";
      if (nextLabel !== labelRef.current) {
        labelRef.current = nextLabel;
        setAiLabel(nextLabel);
      }

      const chatOpacity = clamp01((p - CHAT_IN_START) / (CHAT_IN_END - CHAT_IN_START));
      if (chatLayerRef.current) {
        chatLayerRef.current.style.opacity = String(chatOpacity);
      }

      if (p >= 1) {
        doneFlag = true;
        setShowChat(true);
        return;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => {
      doneFlag = true;
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!showChat) return;

    const timer = setTimeout(() => {
      setStage((prev) => (prev + 1) % STAGE_DURATIONS.length);
    }, STAGE_DURATIONS[stage]);

    return () => clearTimeout(timer);
  }, [showChat, stage]);

  const mapOpen = showChat && stage === 5;

  return (
    <Section>
      <Container>
        <Center>
          <Eyebrow>AI Search</Eyebrow>
          <Headline>Turn scattered paperwork into one answer</Headline>
          <SubCopy>
            Emails, WhatsApp threads, customs portals, spreadsheets — MajurAI
            pulls it all into one AI layer, clears the noise, and answers
            instantly.
          </SubCopy>
        </Center>

        <GameWrap>
          {showChat && (
            <Dots>
              {STAGE_DURATIONS.map((_, index) => (
                <Dot key={index} $active={index === stage} />
              ))}
            </Dots>
          )}

          <FrameWrap>
              <Frame $wide={mapOpen} aria-hidden="true">
                <Chrome>
                  <WindowDots>
                    <WindowDot $color="#ff6b6b" />
                    <WindowDot $color="#ffcf5c" />
                    <WindowDot $color="#4bd07f" />
                  </WindowDots>
                  <UrlPill>
                    <Lock />
                    app.majura.ai/narada
                  </UrlPill>
                </Chrome>

                <ContentArea>
                  <DocsLayer $visible={!showChat}>
                    <AiCore $learned={aiLabel === "Learned"}>
                      <AiCoreLabel>{aiLabel}</AiCoreLabel>
                    </AiCore>
                  </DocsLayer>

                  <ChatLayer ref={chatLayerRef}>
                    <Panes>
                      <ChatPane>
                        <ChatHeader>MajurAI</ChatHeader>

                        <MessageList ref={messageListRef}>
                          <UserRow>
                            <UserBubble>{QUERY}</UserBubble>
                            <UserAvatar aria-hidden="true">
                              <PersonIcon />
                            </UserAvatar>
                          </UserRow>

                          {showChat && stage === 0 && <Camel aria-hidden="true"><CamelIcon src="/assets/img/ai-search/camel-icon.svg" alt="" /></Camel>}

                          {showChat && stage >= 1 && (
                            <AssistantRow>
                              <AssistantAvatar aria-hidden="true"><CamelIcon src="/assets/img/ai-search/camel-icon.svg" alt="" /></AssistantAvatar>
                              <AssistantBlock>
                                <StatusRow>
                                  <StatusIcon />
                                  Found
                                </StatusRow>

                                <TableCard>
                                  <TableSummary>
                                    <strong>44 active lanes analyzed across 6 regional
                                    hubs.</strong> Ranked by complexity and recoverable
                                    cost — not just shipment volume.
                                  </TableSummary>

                                  <MdTable>
                                    <MdTableHeadRow $cols="1.6fr 0.6fr 0.9fr 0.8fr">
                                      <span>Account</span>
                                      <span>Lanes</span>
                                      <span>Shipment</span>
                                      <span>Category</span>
                                    </MdTableHeadRow>
                                    {ACCOUNTS.map((row, index) => (
                                      <MdTableRow
                                        key={row.account}
                                        $alt={index % 2 === 1}
                                        $cols="1.6fr 0.6fr 0.9fr 0.8fr"
                                      >
                                        <RouteCell>{row.account}</RouteCell>
                                        <ShipmentCell>{row.lanes}</ShipmentCell>
                                        <ShipmentCell>{row.shipment}</ShipmentCell>
                                        <CategoryPill $tone={ACCOUNT_TONE[row.category]}>
                                          {row.category}
                                        </CategoryPill>
                                      </MdTableRow>
                                    ))}
                                  </MdTable>

                                  <TableMoreButton type="button" tabIndex={-1}>
                                    <Chevron />
                                    More
                                  </TableMoreButton>
                                </TableCard>
                              </AssistantBlock>
                            </AssistantRow>
                          )}

                          {showChat && stage >= 2 && (
                            <AssistantRow>
                              <AssistantAvatar aria-hidden="true"><CamelIcon src="/assets/img/ai-search/camel-icon.svg" alt="" /></AssistantAvatar>
                              <AssistantBlock>
                                <TableCard>
                                  <MdTable>
                                    <MdTableHeadRow $cols="1.6fr 0.9fr 0.9fr">
                                      <span>Route</span>
                                      <span>Value</span>
                                      <span>Complexity</span>
                                    </MdTableHeadRow>
                                    {ROUTES.map((row, index) => (
                                      <MdTableRow key={row.route} $alt={index % 2 === 1} $cols="1.6fr 0.9fr 0.9fr">
                                        <RouteCell>{row.route}</RouteCell>
                                        <ShipmentCell>{row.value}</ShipmentCell>
                                        <CategoryPill $tone={ROUTE_TONE[row.category as keyof typeof ROUTE_TONE]}>
                                          {row.category}
                                        </CategoryPill>
                                      </MdTableRow>
                                    ))}
                                  </MdTable>

                                  <TableMoreButton type="button" tabIndex={-1}>
                                    <Chevron />
                                    More
                                  </TableMoreButton>
                                </TableCard>

                                <AssistantText>
                                  Jawa Timur carries your highest complexity and your
                                  largest recoverable cost. Want me to break down a
                                  specific lane?
                                </AssistantText>
                              </AssistantBlock>
                            </AssistantRow>
                          )}

                          {showChat && stage >= 3 && (
                            <UserRow>
                              <UserBubble $compact>
                                Let me see the opportunity for Jakarta → Surabaya.
                              </UserBubble>
                              <UserAvatar aria-hidden="true">
                                <PersonIcon />
                              </UserAvatar>
                            </UserRow>
                          )}

                          {showChat && stage >= 4 && (
                            <AssistantRow>
                              <AssistantAvatar aria-hidden="true"><CamelIcon src="/assets/img/ai-search/camel-icon.svg" alt="" /></AssistantAvatar>
                              <AssistantBlock>
                                <StatusRow>
                                  <StatusIcon />
                                  Found TMS + WMS
                                </StatusRow>

                                <AssistantText>
                                  This lane has a potential <strong>Rp 280 million/month
                                  (~$18,000)</strong> cost reduction. Your current network
                                  spreads demand across three regional depots — with
                                  overlapping routes. Here&apos;s a proposed rebalancing,
                                  using only your existing transfer lanes.
                                </AssistantText>

                                <MapChip>
                                  <MapChipIcon />
                                  Generated map — Network Rebalance
                                </MapChip>
                              </AssistantBlock>
                            </AssistantRow>
                          )}

                          {mapOpen && (
                            <AssistantRow>
                              <AssistantAvatar aria-hidden="true"><CamelIcon src="/assets/img/ai-search/camel-icon.svg" alt="" /></AssistantAvatar>
                              <AssistantText style={{ flex: 1, minWidth: 0 }}>
                                Want me to draft the reallocation plan for your
                                logistics team?
                              </AssistantText>
                            </AssistantRow>
                          )}
                        </MessageList>
                      </ChatPane>

                      <MapPane $open={mapOpen}>
                        <MapPaneInner>
                          <MapHeader>
                            <span>Map · Network Rebalance</span>
                            <CloseX>×</CloseX>
                          </MapHeader>

                          <MapCanvas>
                            <MapImg src="/assets/img/ai-search/java-map.png" alt="" aria-hidden="true" />

                            <RouteLines viewBox="0 0 100 100" preserveAspectRatio="none">
                              {NODES.slice(0, -1).map((node, index) => {
                                const next = NODES[index + 1];
                                const midX = (node.x + next.x) / 2;
                                const midY = Math.min(node.y, next.y) - 10;
                                const pathD = `M${node.x},${node.y} Q${midX},${midY} ${next.x},${next.y}`;
                                return (
                                  <g key={node.name} opacity={mapOpen ? 1 : 0}>
                                    <path
                                      d={pathD}
                                      fill="none"
                                      stroke="#2f6fed"
                                      strokeWidth="1.6"
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d={pathD}
                                      fill="none"
                                      stroke="#ffffff"
                                      strokeWidth="0.5"
                                      strokeDasharray="0.4 2.4"
                                      strokeLinecap="round"
                                    />
                                  </g>
                                );
                              })}
                            </RouteLines>

                            {NODES.slice(0, -1).map((node, index) => {
                              const next = NODES[index + 1];
                              const midX = (node.x + next.x) / 2;
                              const midY = Math.min(node.y, next.y) - 10;
                              return (
                                <TransferAmount
                                  key={node.name}
                                  style={{ left: `${midX}%`, top: `${midY}%` }}
                                >
                                  <TransferDot />
                                  {TRANSFERS[index]}
                                </TransferAmount>
                              );
                            })}

                            {NODES.map((node, index) => (
                              <MapNode key={node.name} style={{ left: `${node.x}%`, top: `${node.y}%` }}>
                                {index === 0 ? <PinMarker /> : <NodeDot $tone={node.tone} />}
                                <NodeLabel>{node.name}</NodeLabel>
                                <NodeTag $tone={node.tone}>{node.tag}</NodeTag>
                              </MapNode>
                            ))}

                            <MapAttribution>© OpenStreetMap contributors</MapAttribution>
                          </MapCanvas>

                          <MapBadge>Rp 280M/mo (~$18K) savings found</MapBadge>
                        </MapPaneInner>
                      </MapPane>
                    </Panes>
                  </ChatLayer>
                </ContentArea>
              </Frame>

              <FlyLayer $visible={!showChat} aria-hidden="true">
                {DOC_SOURCES.map((doc, index) => (
                  <DocCard
                    key={doc.label}
                    ref={(el) => {
                      docRefs.current[index] = el;
                    }}
                    style={{
                      left: `${doc.x}%`,
                      top: `${doc.y}%`,
                      transform: `translate(-50%, -50%) rotate(${doc.rot}deg)`
                    }}
                  >
                    <DocGlyph>
                      <span />
                      <span />
                      <span />
                      <span />
                    </DocGlyph>
                    <DocLabel>{doc.label}</DocLabel>
                  </DocCard>
                ))}
              </FlyLayer>
            </FrameWrap>
        </GameWrap>
      </Container>
    </Section>
  );
};

/* ----------------------------------------------------------------- shell */

const Section = styled.div`
  position: relative;
  width: 100%;
  padding: 48px 0 96px;
  background: #fbfcff;

  @media (max-width: 1025px) {
    padding: 32px 0 64px;
  }
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 760px;
  margin: 0 auto;
`;

const Eyebrow = styled.p`
  color: #4f79ff;
  font-weight: 600;
  letter-spacing: 1px;
  font-size: 0.9rem;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const Headline = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  color: #0a1f60;
  margin: 0 0 20px;
  line-height: 1.2;
  white-space: nowrap;

  @media (max-width: 1025px) {
    font-size: 1.5rem;
  }

  @media (max-width: 640px) {
    font-size: 1.15rem;
  }
`;

const SubCopy = styled.p`
  font-size: 1.05rem;
  line-height: 1.6;
  color: #4b5a76;
  margin: 0;
`;

const GameWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 24px 0;
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const Dot = styled.div<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? "22px" : "8px")};
  height: 8px;
  border-radius: 4px;
  background: ${({ $active }) => ($active ? INDIGO : "rgba(11, 28, 117, 0.15)")};
  transition: all 0.3s ease;
`;

const FrameWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 680px;

  @media (max-width: 1025px) {
    max-width: 600px;
  }
`;

/* Sits as a sibling of Frame (not a descendant) so the scattered cards are
   not clipped by Frame's own overflow: hidden — they sweep in from outside
   the window's edges and land on the AI layer drawn inside ContentArea. */
const FlyLayer = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  transition: opacity 0.5s ease, visibility 0s ${({ $visible }) => ($visible ? "0s" : "0.5s")};
`;

const Frame = styled.div<{ $wide: boolean }>`
  width: 100%;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid ${C.hairline};
  box-shadow: 0 28px 64px rgba(11, 28, 117, 0.16);
  overflow: hidden;
`;

const Chrome = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: ${C.chrome};
  border-bottom: 1px solid ${C.hairline};
`;

const WindowDots = styled.div`
  display: flex;
  gap: 5px;
`;

const WindowDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  opacity: 0.7;
`;

const UrlPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  padding: 4px 10px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${C.hairline};
  font-size: 0.68rem;
  font-weight: 500;
  color: ${C.textSub};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Lock = styled.span`
  flex-shrink: 0;
  width: 7px;
  height: 6px;
  border-radius: 1px;
  background: ${C.textSub};
  position: relative;
  opacity: 0.6;

  &::before {
    content: "";
    position: absolute;
    left: 1.5px;
    top: -3px;
    width: 4px;
    height: 4px;
    border: 1px solid ${C.textSub};
    border-bottom: none;
    border-radius: 2px 2px 0 0;
  }
`;

const ContentArea = styled.div`
  position: relative;
  height: 460px;
`;

/* ------------------------------------------------------------ docs layer */

const DocsLayer = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  background: ${C.pageBg};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  transition: opacity 0.5s ease, visibility 0s ${({ $visible }) => ($visible ? "0s" : "0.5s")};
`;

const corePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(18, 176, 122, 0.35); }
  100% { box-shadow: 0 0 0 16px rgba(18, 176, 122, 0); }
`;

const AiCore = styled.div<{ $learned: boolean }>`
  position: absolute;
  left: 50%;
  top: 54%;
  transform: translate(-50%, -50%) scale(${({ $learned }) => ($learned ? 1.08 : 1)});
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $learned }) => ($learned ? "rgba(18, 176, 122, 0.14)" : "rgba(76, 92, 240, 0.12)")};
  border: 1px solid ${({ $learned }) => ($learned ? "rgba(18, 176, 122, 0.4)" : "rgba(76, 92, 240, 0.35)")};
  transition: background 0.4s ease, border-color 0.4s ease, transform 0.4s ease;
  animation: ${({ $learned }) => ($learned ? corePulse : "none")} 0.8s ease-out;
  z-index: 2;
`;

const AiCoreLabel = styled.span`
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${C.textSub};
  text-align: center;
`;

const DocCard = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid ${C.hairline};
  box-shadow: 0 6px 14px rgba(11, 28, 117, 0.08);
  z-index: 1;
  will-change: left, top, transform, opacity;
`;

const DocGlyph = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 16px;

  span {
    width: 2px;
    background: rgba(11, 28, 117, 0.35);

    &:nth-child(1) {
      height: 60%;
    }
    &:nth-child(2) {
      height: 100%;
    }
    &:nth-child(3) {
      height: 45%;
    }
    &:nth-child(4) {
      height: 80%;
    }
  }
`;

const DocLabel = styled.span`
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${C.textSub};
  white-space: nowrap;
`;

/* ------------------------------------------------------------ chat layer */

const ChatLayer = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
`;

const Panes = styled.div`
  display: flex;
  height: 100%;
`;

const ChatPane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: ${C.pageBg};
`;

const ChatHeader = styled.div`
  flex-shrink: 0;
  padding: 10px 36px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${C.textSub};
  border-bottom: 1px solid ${C.divider};
`;

const MessageList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 36px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.divider};
    border-radius: 4px;
  }
`;

/* Real-time-feeling reveal: every new message block fades and rises into
   place instead of popping in, matching the reference video's crossfade
   between "Generating..." and the table/reply landing. */
const messageIn = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const UserRow = styled.div`
  align-self: flex-end;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 88%;
`;

const UserBubble = styled.div<{ $compact?: boolean }>`
  min-width: 0;
  padding: ${({ $compact }) => ($compact ? "7px 11px" : "9px 13px")};
  border-radius: 14px 14px 3px 14px;
  background: ${C.userBubbleBg};
  color: #ffffff;
  font-size: 0.75rem;
  line-height: 1.5;
  animation: ${messageIn} 0.4s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const UserAvatar = styled.div`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${C.hairline};
  color: ${C.textSub};
`;

const PersonIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" fill="currentColor" />
    <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" fill="currentColor" />
  </svg>
);

const camelRun = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(-3deg); }
  50% { transform: translate(4px, -2px) rotate(2deg); }
`;

const Camel = styled.div`
  align-self: flex-start;
  width: 15px;
  height: 15px;
  animation: ${camelRun} 0.55s ease-in-out infinite, ${messageIn} 0.4s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CamelIcon = styled.img`
  width: 15px;
  height: 15px;
  object-fit: contain;
  display: block;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-size: 0.68rem;
  font-weight: 700;
  color: ${INDIGO};
  animation: ${messageIn} 0.4s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StatusIcon = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${INDIGO};
  box-shadow: 0 0 0 3px rgba(76, 92, 240, 0.18);
`;

const AssistantRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const AssistantAvatar = styled.div`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(76, 92, 240, 0.12);
`;

const AssistantBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
  animation: ${messageIn} 0.45s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const AssistantText = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.55;
  color: ${C.text};
  animation: ${messageIn} 0.45s ease-out 0.12s both;

  strong {
    color: ${NAVY};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MapChip = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  align-self: flex-start;
  padding: 6px 11px;
  border-radius: 10px;
  border: 1px solid ${C.divider};
  font-size: 0.68rem;
  font-weight: 600;
  color: ${C.textSub};
`;

const MapChipIcon = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: rgba(76, 92, 240, 0.16);
  border: 1px solid rgba(76, 92, 240, 0.4);
`;

/* ------------------------------------------------------------- table card */

const TableCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid ${C.divider};
  overflow: hidden;
  background: #ffffff;
`;

const TableSummary = styled.div`
  padding: 9px 11px;
  font-size: 0.66rem;
  line-height: 1.5;
  color: ${C.textSub};
  background: ${C.pageBg};
  border-bottom: 1px solid ${C.divider};

  strong {
    color: ${C.text};
  }
`;

const MdTable = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.64rem;
`;

const MdTableHeadRow = styled.div<{ $cols?: string }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || "1.6fr 1fr 0.8fr 0.9fr"};
  gap: 6px;
  padding: 8px 11px;
  font-weight: 700;
  color: ${C.textMuted};
  background: ${C.pageBg};
  border-bottom: 1px solid ${C.divider};
`;

const MdTableRow = styled.div<{ $alt?: boolean; $cols?: string }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || "1.6fr 1fr 0.8fr 0.9fr"};
  align-items: center;
  gap: 6px;
  padding: 8px 11px;
  background: ${({ $alt }) => ($alt ? C.pageBg : "#ffffff")};

  & + & {
    border-top: 1px solid ${C.divider};
  }
`;

const RouteCell = styled.span`
  font-weight: 600;
  color: ${NAVY};
`;

const ShipmentCell = styled.span`
  color: ${C.textSub};
  font-variant-numeric: tabular-nums;
`;

const CATEGORY_TONE_COLOR: Record<"good" | "warn" | "info" | "neutral", { fg: string; bg: string }> = {
  good: { fg: GOOD, bg: "rgba(18, 176, 122, 0.12)" },
  warn: { fg: WARN, bg: "rgba(224, 138, 44, 0.14)" },
  info: { fg: INDIGO, bg: "rgba(76, 92, 240, 0.12)" },
  neutral: { fg: C.textSub, bg: "rgba(0, 0, 0, 0.05)" }
};

const CategoryPill = styled.span<{ $tone: "good" | "warn" | "info" | "neutral" }>`
  justify-self: start;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.58rem;
  font-weight: 700;
  color: ${({ $tone }) => CATEGORY_TONE_COLOR[$tone].fg};
  background: ${({ $tone }) => CATEGORY_TONE_COLOR[$tone].bg};
`;

const TableMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px;
  border: none;
  border-top: 1px solid ${C.divider};
  background: ${C.pageBg};
  color: ${C.textSub};
  font-size: 0.62rem;
  font-weight: 700;
  cursor: default;
`;

const Chevron = styled.span`
  width: 6px;
  height: 6px;
  border-right: 1.5px solid ${C.textSub};
  border-bottom: 1.5px solid ${C.textSub};
  transform: rotate(45deg) translateY(-1px);
`;

/* ------------------------------------------------------------- map pane */

const MapPane = styled.div<{ $open: boolean }>`
  flex-shrink: 0;
  width: ${({ $open }) => ($open ? "320px" : "0")};
  overflow: hidden;
  border-left: ${({ $open }) => ($open ? `1px solid ${C.hairline}` : "none")};
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);

  @media (max-width: 1025px) {
    width: ${({ $open }) => ($open ? "300px" : "0")};
  }
`;

const MapPaneInner = styled.div`
  width: 320px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const MapHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${C.text};
  border-bottom: 1px solid ${C.divider};
`;

const CloseX = styled.span`
  color: ${C.textMuted};
  font-size: 0.9rem;
  line-height: 1;
`;

const MapCanvas = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  background: #cfe3f5;
`;

const MapImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MapAttribution = styled.span`
  position: absolute;
  right: 6px;
  bottom: 4px;
  z-index: 3;
  font-size: 0.46rem;
  color: rgba(0, 0, 0, 0.55);
  background: rgba(255, 255, 255, 0.65);
  padding: 1px 5px;
  border-radius: 4px;
`;

const RouteLines = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const MapNode = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  transform: translate(-50%, -50%);
  width: 74px;
  z-index: 2;
`;

const NodeDot = styled.div<{ $tone: "warn" | "info" }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $tone }) => ($tone === "warn" ? WARN : "#ffffff")};
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 3.5px
    ${({ $tone }) => ($tone === "warn" ? WARN : INDIGO)};
`;

const NodeLabel = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
`;

const NodeTag = styled.span<{ $tone: "warn" | "info" }>`
  font-size: 0.5rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  color: #ffffff;
  background: ${({ $tone }) => ($tone === "warn" ? WARN : INDIGO)};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
`;

const TransferAmount = styled.span`
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.56rem;
  font-weight: 700;
  color: ${C.text};
  white-space: nowrap;
  background: #ffffff;
  padding: 3px 8px 3px 6px;
  border-radius: 7px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(20, 30, 60, 0.22);
`;

const TransferDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2f6fed;
  flex-shrink: 0;
`;

const PinMarker = styled.span`
  width: 12px;
  height: 16px;
  position: relative;
  display: block;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: #e0442f;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ffffff;
  }
`;

const MapBadge = styled.div`
  flex-shrink: 0;
  margin: 10px 12px 12px;
  padding: 6px 10px;
  border-radius: 999px;
  text-align: center;
  font-size: 0.6rem;
  font-weight: 700;
  color: ${GOOD};
  background: rgba(18, 176, 122, 0.12);
`;

export default AISearchSection;
